/**
 * Upload a file to a presigned S3 URL with progress reporting.
 * Uses XMLHttpRequest so we can listen to upload.onprogress (fetch does not expose it).
 *
 * Two modes:
 * - **PUT** (default): the raw file is the body, for a presigned PUT URL.
 * - **POST** (CEE-1205, TAR §2.3): a `multipart/form-data` body for an S3 presigned POST. The
 *   policy fields go first and the file goes LAST; S3 ignores every form field after `file`, so
 *   the order is a requirement, not a style choice.
 *
 * Cancel with `options.signal`: the request is aborted and the promise rejects with a
 * `DOMException` named `AbortError`, the same shape `fetch` uses.
 */

export interface UploadProgressCallback {
  (percent: number): void;
}

export interface UploadFileWithProgressOptions {
  /**
   * Extra request headers (e.g. S3 SSE from the authoring API). In POST mode `Content-Type` is
   * ignored: the browser must set the multipart boundary itself.
   */
  headers?: Record<string, string>;
  /** `PUT` (default) sends the file as the body; `POST` sends a multipart form. */
  method?: 'PUT' | 'POST';
  /** Presigned POST policy fields, appended before the file. Only used in POST mode. */
  formFields?: Record<string, string>;
  /** Aborts the upload; the promise then rejects with an `AbortError`. */
  signal?: AbortSignal;
}

/**
 * Statuses the Fetch spec forbids a body on. `new Response('', { status: 204 })` throws, and 204 is
 * what an S3 presigned POST answers by default, so these get a null body.
 */
const NULL_BODY_STATUSES = new Set([101, 103, 204, 205, 304]);

function abortError(): DOMException {
  return new DOMException('Upload aborted', 'AbortError');
}

/** Multipart body for an S3 presigned POST: every policy field first, then the file. */
function buildPostBody(file: File, formFields: Record<string, string> = {}): FormData {
  const form = new FormData();
  Object.entries(formFields).forEach(([key, value]) => {
    form.append(key, value);
  });
  form.append('file', file);
  return form;
}

/**
 * Upload file to presigned S3 URL with progress callback.
 * @param uploadUrl - Presigned PUT URL, or the presigned POST form action URL
 * @param file - File to upload
 * @param onProgress - Called with 0-100 as upload progresses
 * @param options - Method, POST form fields, extra headers and an abort signal
 * @returns Promise that resolves when upload completes, rejects on error or abort
 */
export function uploadFileWithProgress(
  uploadUrl: string,
  file: File,
  onProgress: UploadProgressCallback,
  options: UploadFileWithProgressOptions = {},
): Promise<Response> {
  return new Promise((resolve, reject) => {
    const { signal } = options;
    if (signal?.aborted) {
      reject(abortError());
      return;
    }

    const method = options.method ?? 'PUT';
    const xhr = new XMLHttpRequest();

    const onAbortSignal = () => xhr.abort();
    const cleanup = () => signal?.removeEventListener('abort', onAbortSignal);

    xhr.responseType = 'text';

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && event.total > 0) {
        const percent = Math.round((event.loaded / event.total) * 100);
        onProgress(Math.min(percent, 100));
      }
    });

    xhr.addEventListener('load', () => {
      cleanup();
      const body = NULL_BODY_STATUSES.has(xhr.status) ? null : xhr.responseText;
      const response = new Response(body, {
        status: xhr.status,
        statusText: xhr.statusText,
        headers: new Headers(
          xhr
            .getAllResponseHeaders()
            .split('\r\n')
            .filter(Boolean)
            .map((line) => {
              const [key, ...parts] = line.split(': ');
              return [key, parts.join(': ')];
            })
            .filter(([k]) => k) as [string, string][]
        )
      });
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(response);
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      cleanup();
      reject(new Error('Network error during upload'));
    });

    xhr.addEventListener('abort', () => {
      cleanup();
      reject(signal?.aborted ? abortError() : new Error('Upload aborted'));
    });

    signal?.addEventListener('abort', onAbortSignal, { once: true });

    xhr.open(method, uploadUrl);

    if (method === 'POST') {
      Object.entries(options.headers ?? {}).forEach(([key, value]) => {
        if (key.toLowerCase() !== 'content-type') {
          xhr.setRequestHeader(key, value);
        }
      });
      xhr.send(buildPostBody(file, options.formFields));
      return;
    }

    const contentType =
      options.headers?.['Content-Type'] ?? (file.type || 'application/octet-stream');
    xhr.setRequestHeader('Content-Type', contentType);
    Object.entries(options.headers ?? {}).forEach(([key, value]) => {
      if (key !== 'Content-Type') {
        xhr.setRequestHeader(key, value);
      }
    });
    xhr.send(file);
  });
}
